// @ts-ignore
import React, { useEffect } from "react";

import { useIntl } from "react-intl";

import {
    getThemeStyles,
    getThemeHtmlAttrs,
    getSSRAutoScript,
    setGlobalTheme
} from "@atlaskit/tokens";

type PageWrapperProps = React.PropsWithChildren<{
    baseUrl: string;
    route: string;
    inlineCss?: string;
    pureClientMode: boolean;
    themeStyles?: Awaited<ReturnType<typeof getThemeStyles>>;
}>;

const SUPPORTED_LOCALES = [
    { hrefLang: "ja", langRoute: "ja" },
    { hrefLang: "fr", langRoute: "fr" },
    { hrefLang: "de", langRoute: "de" },
    { hrefLang: "es", langRoute: "es" },
    { hrefLang: "pt-BR", langRoute: "br" },
    { hrefLang: "zh-Hans", langRoute: "zh" },
    { hrefLang: "ko", langRoute: "ko" },
    { hrefLang: "ru", langRoute: "ru" },
    { hrefLang: "pl", langRoute: "pl" },
    { hrefLang: "it", langRoute: "it" },
    { hrefLang: "nl", langRoute: "nl" },
    { hrefLang: "hu", langRoute: "hu" }
];

export const PageWrapper = (props: PageWrapperProps) => {
    const { baseUrl, route, inlineCss, themeStyles, children } = props;
    const intl = useIntl();

    const themePrefs = { colorMode: "light" } as const;
    const themeAttrs = getThemeHtmlAttrs(themePrefs);
    const ssrAutoScript = getSSRAutoScript(themePrefs.colorMode);

    useEffect(() => {
        // Theme config for client-only rendering
        setGlobalTheme(themePrefs);
    }, []);

    let title, description;
    if (route === "/software/jira") {
        title = intl.formatMessage({
            id: "JiraSoftwarePage.title",
            defaultMessage:
                "Jira | Issue & Project Tracking Software | Atlassian"
        });
        description = intl.formatMessage({
            id: "JiraSoftwarePage.description",
            defaultMessage:
                "Make the impossible, possible in Jira. Plan, track, and release world-class software with the number one project management tool for agile teams."
        });
    } else if (route === "/software/confluence") {
        title = intl.formatMessage({
            id: "ConfluencePage.title",
            defaultMessage:
                "Confluence | Your Remote-Friendly Team Workspace | Atlassian"
        });
        description = intl.formatMessage({
            id: "ConfluencePage.description",
            defaultMessage:
                "Make the impossible, possible with Confluence Cloud. Your team workspace trusted for documentation, project collaboration, Jira integrations, and more!"
        });
    } else if (route === "/ondemand/signup/confirmation") {
        title = intl.formatMessage({
            id: "ConfirmationPage.title",
            defaultMessage: "Confirmation | Atlassian"
        });
        description = intl.formatMessage({
            id: "ConfirmationPage.description",
            defaultMessage:
                "The #1 software development tool used by agile teams. Plan, track, and release world-class software."
        });
    } else if (route === "/ondemand/signup/checkemail") {
        title = intl.formatMessage({
            id: "CheckemailPage.title",
            defaultMessage: "Check Email | Atlassian"
        });
        description = intl.formatMessage({
            id: "CheckemailPage.description",
            defaultMessage:
                "Millions of users globally rely on Atlassian products every day for improving software development, project management, collaboration, and code quality."
        });
    } else {
        title = intl.formatMessage({
            id: "HomePage.title",
            defaultMessage:
                "Collaboration software for software, IT and business teams"
        });
        description = intl.formatMessage({
            id: "HomePage.description",
            defaultMessage:
                "Atlassian's team collaboration software like Jira, Confluence and Trello help teams organize, discuss, and complete shared work."
        });
    }

    const localePrefix = intl.locale === "en" ? "" : `/${intl.locale}`;

    if (props.pureClientMode) {
        return (
            <>
                <style
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{ __html: inlineCss || "" }}
                />
                <link rel="canonical" href={`${baseUrl}${route}`} />
                <link
                    rel="alternate"
                    href={`${baseUrl}${route}`}
                    hrefLang="x-default"
                />
                {SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
                    <link
                        key={hrefLang}
                        rel="alternate"
                        href={`${baseUrl}/${langRoute}${route}`}
                        hrefLang={hrefLang}
                    />
                ))}
                {children}
            </>
        );
    }

    return (
        // eslint-disable-next-line jsx-a11y/html-has-lang
        <html {...themeAttrs}>
            <head>
                <meta charSet="utf-8" />
                <title>{title}</title>
                <meta name="description" content={description} />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge, chrome=1" />
                <style
                    suppressHydrationWarning
                    dangerouslySetInnerHTML={{ __html: inlineCss || "" }}
                />
                <link
                    rel="canonical"
                    href={`${baseUrl}${localePrefix}${route}`}
                />
                <link
                    rel="alternate"
                    href={`${baseUrl}${route}`}
                    hrefLang="x-default"
                />
                {SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
                    <link
                        key={hrefLang}
                        rel="alternate"
                        href={`${baseUrl}/${langRoute}${route}`}
                        hrefLang={hrefLang}
                    />
                ))}
                {(themeStyles || []).map((theme) => (
                    <style
                        key={theme.id}
                        {...theme.attrs}
                        dangerouslySetInnerHTML={{ __html: theme.css }}
                    />
                ))}
                {ssrAutoScript ? (
                    <script
                        dangerouslySetInnerHTML={{ __html: ssrAutoScript }}
                    />
                ) : null}
            </head>
            <body>{children}</body>
        </html>
    );
};
